import { useQuery } from '@tanstack/react-query';
import eodhd from '../services/eodhd';
import type { TaAnalysis } from '../services/eodhd';

interface Props {
  symbol: string;
  interval?: string;
}

const RECOMMENDATION_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  STRONG_BUY:  { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Strong Buy' },
  BUY:         { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Buy' },
  NEUTRAL:     { bg: 'bg-slate-700/50',   text: 'text-slate-300',   label: 'Neutral' },
  SELL:        { bg: 'bg-red-500/10',     text: 'text-red-400',     label: 'Sell' },
  STRONG_SELL: { bg: 'bg-red-500/20',     text: 'text-red-400',     label: 'Strong Sell' },
};

function IndicatorRow({ label, value, suffix = '' }: { label: string; value: number | undefined; suffix?: string }) {
  if (value == null) return null;
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-xs font-mono text-slate-200">{value.toFixed(2)}{suffix}</span>
    </div>
  );
}

export default function TaAnalysisCard({ symbol, interval = '1d' }: Props) {
  const { data: ta, isLoading, error } = useQuery<TaAnalysis | null>({
    queryKey: ['taAnalysis', symbol, interval],
    queryFn: () => eodhd.getTaAnalysis(symbol, interval),
    staleTime: 15 * 60 * 1000, // 15 min — matches Redis TTL in backend
    retry: false,
    enabled: !!symbol, // skip query when symbol is undefined/empty
  });

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4 animate-pulse">
        <div className="h-4 bg-slate-700 rounded w-40 mb-3" />
        <div className="flex gap-3">
          <div className="h-16 w-24 bg-slate-700 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-slate-700 rounded w-full" />
            <div className="h-3 bg-slate-700 rounded w-3/4" />
            <div className="h-3 bg-slate-700 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !ta) return null;

  const style = RECOMMENDATION_STYLE[ta.recommendation] ?? RECOMMENDATION_STYLE.NEUTRAL;
  const hasCounts = ta.buy_count + ta.sell_count + ta.neutral_count > 0;
  const ind = ta.indicators;

  // EMA cross check
  const emaCross = ind.EMA20 != null && ind.EMA50 != null
    ? ind.EMA20 > ind.EMA50 ? 'Bullish' : 'Bearish'
    : null;

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
        TradingView Analysis
      </h3>

      <div className="flex gap-3">
        {/* Recommendation pill */}
        <div className={`flex-shrink-0 flex flex-col items-center justify-center rounded-lg px-3 py-2 min-w-[80px] ${style.bg}`}>
          <span className={`text-[11px] font-bold uppercase tracking-wide ${style.text}`}>
            {style.label}
          </span>
          {hasCounts && (
            <span className="text-[10px] text-slate-500 mt-1">
              {ta.buy_count}B · {ta.sell_count}S · {ta.neutral_count}N
            </span>
          )}
        </div>

        {/* Indicators */}
        <div className="flex-1 space-y-1.5">
          <IndicatorRow label="RSI (14)" value={ind.RSI} />
          <IndicatorRow label="MACD" value={ind['MACD.macd']} />
          <IndicatorRow label="ADX" value={ind.ADX} />
          {emaCross && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">EMA20/50</span>
              <span className={`text-xs font-mono ${emaCross === 'Bullish' ? 'text-emerald-400' : 'text-red-400'}`}>
                {emaCross}
              </span>
            </div>
          )}
          <IndicatorRow label="Stoch K" value={ind['Stoch.K']} />
        </div>
      </div>

      <p className="text-[10px] text-slate-600 mt-3">
        {interval.toUpperCase()} · via TradingView · cached 15 min
      </p>
    </div>
  );
}
